import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../config/api';
import { debugError } from '../utils/debugLog';

/**
 * Custom hook for fetching analytics data for the governance dashboard
 *
 * Provides:
 * - Summary statistics (total transactions, violations, etc.)
 * - Chart data for various visualizations
 * - Loading and error states
 * - Automatic refresh capabilities
 */
export const useAnalyticsData = (refreshInterval = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch summary statistics
      const summaryResponse = await apiFetch('/api/v1/analytics/stats');

      // Fetch chart data
      const chartsResponse = await apiFetch('/api/v1/analytics/analytics');

      if (summaryResponse.success && chartsResponse.success) {
        const analyticsData = {
          // Summary statistics (using correct API response structure)
          totalTransactions: summaryResponse.data?.total_transactions || 0,
          totalViolations: summaryResponse.data?.total_violations || 0,
          cleanTransactions: summaryResponse.data?.clean_transactions || 0,
          anonymizedTransactions:
            summaryResponse.data?.anonymized_transactions || 0,
          averageRiskScore: summaryResponse.data?.average_risk_score || 0,
          averageComplianceRate:
            summaryResponse.data?.average_compliance_rate || 0,
          violationRate: summaryResponse.data?.violation_rate || 0,
          anonymizationRate: summaryResponse.data?.anonymization_rate || 0,
          earliestTransaction: summaryResponse.data?.earliest_transaction,
          latestTransaction: summaryResponse.data?.latest_transaction,

          // Chart data
          anonymizationStats: chartsResponse.data?.anonymization_stats || [],
          violationStats: chartsResponse.data?.violation_stats || [],
          weeklyTransactions: chartsResponse.data?.weekly_transactions || [],
          distributionStats: chartsResponse.data?.distribution_stats || [],
        };

        setData(analyticsData);
      } else {
        setError('Failed to fetch analytics data');
      }
    } catch (err) {
      debugError('Analytics data fetch error:', err);
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalyticsData();

    // Set up automatic refresh if interval is provided
    let intervalId = null;
    if (refreshInterval) {
      intervalId = setInterval(fetchAnalyticsData, refreshInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchAnalyticsData, refreshInterval]);

  const refresh = () => {
    fetchAnalyticsData();
  };

  return {
    data,
    loading,
    error,
    refresh,
  };
};
